/**
 * 组织成员列表组件
 * 支持：搜索、添加成员、转移成员、设负责人、移除
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Space, Input, Tag, Avatar, message, Popconfirm,
  Modal, Select, TreeSelect,
} from 'antd';
import { modal } from '@/utils/antdStatic';
import {
  UserOutlined, DeleteOutlined, CrownOutlined, SearchOutlined,
  PlusOutlined, SwapOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getOrganizationMembers,
  removeMembersFromOrganization,
  updateMemberPosition,
  addMembersToOrganization,
  transferMembers,
  getOrganizationTree,
} from '../../api/organization';
import { getUserList } from '../../api/user';
import type { OrganizationMember, OrganizationTreeNode } from '../../api/types';

interface MemberListProps {
  organizationId: string;
  onMemberChange?: () => void;
}

/** 将组织树转为 TreeSelect 数据 */
const convertOrgTreeData = (nodes: OrganizationTreeNode[]): any[] =>
  nodes.map((n) => ({
    value: n.key || n.value,
    title: n.title,
    children: n.children?.length ? convertOrgTreeData(n.children) : undefined,
  }));

const MemberList: React.FC<MemberListProps> = ({ organizationId, onMemberChange }) => {
  const [list, setList] = useState<OrganizationMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [searchName, setSearchName] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  // 添加成员
  const [addVisible, setAddVisible] = useState(false);
  const [addUserIds, setAddUserIds] = useState<string[]>([]);
  const [userOptions, setUserOptions] = useState<{ value: string; label: string }[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  // 转移成员
  const [transferVisible, setTransferVisible] = useState(false);
  const [transferTargetOrgId, setTransferTargetOrgId] = useState<string>('');
  const [orgTreeData, setOrgTreeData] = useState<any[]>([]);

  /** 获取成员列表 */
  const fetchMembers = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const res = await getOrganizationMembers(organizationId, {
        current: pagination.current,
        pageSize: pagination.pageSize,
        userName: searchName || undefined,
      });
      if (res?.data) {
        const data = res.data as any;
        // ERP returns {items:[...], total:N}; foundation may return {list:[...], total:N}
        setList(data.items || data.list || []);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('获取组织成员失败', error);
    } finally {
      setLoading(false);
    }
  }, [organizationId, pagination.current, pagination.pageSize, searchName]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  /** 搜索 */
  const handleSearch = (value: string) => {
    setSearchName(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  /** 设为负责人 */
  const handleSetLeader = async (member: OrganizationMember) => {
    try {
      await updateMemberPosition(organizationId, member.userId, {
        isLeader: true,
      });
      message.success('已设为负责人');
      fetchMembers();
      onMemberChange?.();
    } catch (error) {
      console.error('设为负责人失败', error);
    }
  };

  /** 移除成员 */
  const handleRemove = async (member: OrganizationMember) => {
    try {
      await removeMembersFromOrganization(organizationId, [member.userId]);
      message.success('移除成功');
      fetchMembers();
      onMemberChange?.();
    } catch (error) {
      console.error('移除成员失败', error);
    }
  };

  /** 表格分页变更 */
  const handleTableChange = (paginationInfo: any) => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    });
  };

  /** 加载用户列表（添加成员用），过滤掉已在组织中的成员 */
  const loadUserOptions = async (keyword?: string) => {
    setUserSearchLoading(true);
    try {
      const params: any = { current: 1, pageSize: 200 };
      if (keyword) params.realName = keyword;
      const res: any = await getUserList(params);
      const data = res?.data;
      const allUsers = Array.isArray(data) ? data : (data?.list || data?.data || []);
      // Filter out users already in this org to prevent 400 "用户已在该组织中"
      const existingIds = new Set(list.map((m: any) => String(m.userId || m.user_id || m.id)));
      setUserOptions(
        allUsers
          .filter((u: any) => !existingIds.has(String(u.id)))
          .map((u: any) => ({
            value: String(u.id),
            label: `${u.realName || u.username}${u.username ? ` (${u.username})` : ''}`,
          }))
      );
    } catch {
      setUserOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  /** 添加成员确认 */
  const handleAddMembers = async () => {
    if (!addUserIds.length) { message.warning('请选择用户'); return; }
    try {
      await addMembersToOrganization(organizationId, { userIds: addUserIds });
      message.success('添加成功');
      setAddVisible(false);
      setAddUserIds([]);
      fetchMembers();
      onMemberChange?.();
    } catch {
      message.error('添加失败');
    }
  };

  /** 打开转移弹窗 */
  const handleOpenTransfer = async () => {
    if (!selectedRowKeys.length) { message.warning('请先选择成员'); return; }
    try {
      const res: any = await getOrganizationTree();
      const data = res?.data?.data || res?.data;
      const nodes = Array.isArray(data) ? data : data?.list || [];
      setOrgTreeData(convertOrgTreeData(nodes));
    } catch {
      setOrgTreeData([]);
    }
    setTransferTargetOrgId('');
    setTransferVisible(true);
  };

  /** 转移成员确认 */
  const handleTransferMembers = async () => {
    if (!transferTargetOrgId) { message.warning('请选择目标组织'); return; }
    try {
      await transferMembers(organizationId, transferTargetOrgId, selectedRowKeys.map(String));
      message.success('转移成功');
      setTransferVisible(false);
      setSelectedRowKeys([]);
      fetchMembers();
      onMemberChange?.();
    } catch {
      message.error('转移失败');
    }
  };

  /** 批量移除 */
  const handleBatchRemove = () => {
    if (!selectedRowKeys.length) { message.warning('请先选择成员'); return; }
    modal.confirm({
      title: `确定移除选中的 ${selectedRowKeys.length} 名成员？`,
      onOk: async () => {
        try {
          await removeMembersFromOrganization(organizationId, selectedRowKeys.map(String));
          message.success('移除成功');
          setSelectedRowKeys([]);
          fetchMembers();
          onMemberChange?.();
        } catch {
          message.error('移除失败');
        }
      },
    });
  };

  /** 成员状态标签 */
  const getStatusTag = (status: string) => {
    const map: Record<string, { color: string; text: string }> = {
      active: { color: 'success', text: '在职' },
      inactive: { color: 'default', text: '离职' },
      transferred: { color: 'warning', text: '已调动' },
    };
    const info = map[status] || { color: 'default', text: status };
    return <Tag color={info.color}>{info.text}</Tag>;
  };

  const columns: ColumnsType<OrganizationMember> = [
    {
      title: '头像',
      dataIndex: 'avatar',
      width: 60,
      render: (avatar) => (
        <Avatar src={avatar} icon={<UserOutlined />} size="small" />
      ),
    },
    {
      title: '姓名',
      dataIndex: 'userName',
      width: 120,
      render: (name, record) => (
        <Space>
          {name}
          {record.isLeader && (
            <Tag color="gold" icon={<CrownOutlined />}>负责人</Tag>
          )}
        </Space>
      ),
    },
    {
      title: '工号',
      dataIndex: 'userCode',
      width: 100,
    },
    {
      title: '职位',
      dataIndex: 'positionName',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: '岗位头衔',
      dataIndex: 'jobTitle',
      width: 120,
      render: (text) => text || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (status) => getStatusTag(status),
    },
    {
      title: '加入日期',
      dataIndex: 'joinDate',
      width: 120,
    },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          {!record.isLeader && (
            <Popconfirm
              title="确定要将该成员设为负责人吗?"
              onConfirm={() => handleSetLeader(record)}
              okText="确定"
              cancelText="取消"
            >
              <Button type="link" icon={<CrownOutlined />} size="small">
                设为负责人
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="确定要移除该成员吗?"
            onConfirm={() => handleRemove(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">
              移除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Input.Search
          placeholder="搜索成员姓名"
          allowClear
          onSearch={handleSearch}
          style={{ width: 260 }}
          prefix={<SearchOutlined />}
        />
        <Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setAddVisible(true); setAddUserIds([]); loadUserOptions(); }}>
            添加成员
          </Button>
          <Button icon={<SwapOutlined />} disabled={!selectedRowKeys.length} onClick={handleOpenTransfer}>
            转移成员 {selectedRowKeys.length > 0 && `(${selectedRowKeys.length})`}
          </Button>
          <Button danger icon={<DeleteOutlined />} disabled={!selectedRowKeys.length} onClick={handleBatchRemove}>
            批量移除
          </Button>
        </Space>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
        pagination={{
          ...pagination,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (t) => `共 ${t} 条`,
        }}
        onChange={handleTableChange}
        size="small"
        scroll={{ x: 900 }}
      />

      {/* 添加成员弹窗 */}
      <Modal
        title="添加成员"
        open={addVisible}
        onOk={handleAddMembers}
        onCancel={() => setAddVisible(false)}
        width={500}
      >
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="输入姓名或邮箱搜索用户"
          value={addUserIds}
          onChange={setAddUserIds}
          onSearch={(v) => loadUserOptions(v || undefined)}
          options={userOptions}
          loading={userSearchLoading}
          filterOption={false}
          showSearch
          notFoundContent={userSearchLoading ? '搜索中...' : '暂无用户'}
        />
      </Modal>

      {/* 转移成员弹窗 */}
      <Modal
        title={`转移 ${selectedRowKeys.length} 名成员到其他组织`}
        open={transferVisible}
        onOk={handleTransferMembers}
        onCancel={() => setTransferVisible(false)}
        width={500}
      >
        <TreeSelect
          style={{ width: '100%' }}
          placeholder="选择目标组织"
          value={transferTargetOrgId || undefined}
          onChange={setTransferTargetOrgId}
          treeData={orgTreeData}
          showSearch
          treeNodeFilterProp="title"
          allowClear
        />
      </Modal>
    </div>
  );
};

export default MemberList;
